import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { User, VerificationStatus } from 'src/shared/types';
import { DEFAULT_HEADERS } from 'src/shared/constants';
import {jwtDecode} from 'jwt-decode';
import {differenceInDays} from 'date-fns';

const useAuth = () => {
  const location = useLocation();
  const navigate = useNavigate();
	const [currentUser, setCurrentUser] = useState<any>(undefined);

  const getToken = () => localStorage.getItem('token');

  const getUser = (): User => {
    const user = localStorage.getItem('user');
    if (user) {
			setCurrentUser(user);
			return JSON.parse(user);
    }
    return {};
  };

  const isAuthenticated = useCallback((): boolean => {
    const token = getToken();
	if (token) {
		const decodedToken = jwtDecode(token);
		const days = differenceInDays(new Date(decodedToken.exp! * 1000), new Date());
		if (days < 3) {
			localStorage.clear();
			return false;
		}
	}

    return !!token;
  }, [currentUser?.id]);

  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(isAuthenticated());

  const setAuthToken = (token: string | undefined) => {
    if (token) {
      localStorage.setItem('token', token);
      setIsLoggedIn(true);
    } else {
      localStorage.removeItem('token');
    }
  };

  const setAuthUser = (user: string | undefined) => {
    if (user) {
      localStorage.setItem('user', user);
      setIsLoggedIn(true);
    } else {
      localStorage.removeItem('user');
    }
  };

  const invalidateUser = () => {
    setAuthToken(undefined);
    setAuthUser(undefined);
    setIsLoggedIn(false);
    navigate(location.pathname);
  };

	const sendSMS = async () => {
		const token = getToken();
		const data = await fetch(`${import.meta.env.VITE_API_URL}/users/phone/verification_code`, {
			method: 'GET',
			headers: {
				...DEFAULT_HEADERS,
				Authorization: `Bearer ${token}`,
			},
		});
		return data?.status === 200;
	}

	const updatePhoneNumber = async (phoneNumber: string) => {
		const token = getToken();
		const data = await fetch(`${import.meta.env.VITE_API_URL}/users/phone`, {
			method: 'POST',
			headers: {
				...DEFAULT_HEADERS,
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({phone: phoneNumber})
		});
		await refreshUser();
		return data?.status === 200 || data?.status === 201;
	}

	const verifyPhone = async (code: string) => {
		const token = getToken();
		const url = new URL(`${import.meta.env.VITE_API_URL}/users/phone/verify`);
		url.searchParams.append('code', code);

		const data = await fetch(url.toString(), {
			method: 'GET',
			headers: {
				...DEFAULT_HEADERS,
				Authorization: `Bearer ${token}`,
			},
		});
		if (data?.status === 200) {
			localStorage.setItem('user.phone.status', VerificationStatus.VERIFIED);
			return true
		}
		return await data.json();
	}

	const refreshUser = async () => {
		const token = getToken();
		const data = await fetch(`${import.meta.env.VITE_API_URL}/users/current`, {
			method: 'GET',
			headers: {
				...DEFAULT_HEADERS,
				Authorization: `Bearer ${token}`,
			},
		});
		if (data.status === 200) {
			const user = await data.json();
			setCurrentUser(user);
			setAuthUser(JSON.stringify(user));
		} else {
			invalidateUser();
		}
	}

	const updateUser = async (user: User): Promise<any> => {
		const payload = {
			id: user.id,
			firstName: user.firstName,
			lastName: user.lastName,
			phone: user.phone!.value,
			email: user.email!.value,
			roles: [...(user.roles || [])]
		}
		const token = getToken();
		const response = await fetch(`${import.meta.env.VITE_API_URL}/users`, {
			method: 'PUT',
			headers: {
				...DEFAULT_HEADERS,
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify(payload),
		});
		return await response.json();
	}

	const emailVerificationToken = async () => {
		const token = getToken();
		const data = await fetch(`${import.meta.env.VITE_API_URL}/users/email/verification_token`, {
			method: 'GET',
			headers: {
				...DEFAULT_HEADERS,
				Authorization: `Bearer ${token}`,
			},
		});

		if (data.status === 200) {
			return Promise.resolve(true);
		}

	}
	const verifyEmail = async (emailToken: string) => {
		const token = getToken();
		const data = await fetch(`${import.meta.env.VITE_API_URL}/users/email/verify?token=${emailToken}`, {
			method: 'GET',
			headers: {
				...DEFAULT_HEADERS,
				Authorization: `Bearer ${token}`,
			},
		});

		if (data.status === 200) {
			return Promise.resolve(true);
		} else {
			return Promise.reject(await data.json());
		}

	}

  return {
    setAuthToken,
    setAuthUser,
    invalidateUser,
    getToken,
    isAuthenticated,
    getUser,
		sendSMS,
		verifyPhone,
		updatePhoneNumber,
		refreshUser,
		updateUser,
		emailVerificationToken,
		verifyEmail,
    isLoggedIn,
  };
};

export default useAuth;
